import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService, ApiResponse } from './api.service';
import { environment } from '../../../environments/environment';

export interface MediaItem {
	id: string;
	uploadedBy: string;
	fileName: string;
	originalName: string;
	filePath: string;
	fileUrl: string;
	mimeType: string;
	fileSize: number;
	alt?: string;
	description?: string;
	createdAt: string;
	updatedAt: string;
}

export interface MediaListResponse {
	data: MediaItem[];
	pagination: {
		total: number;
		page: number;
		limit: number;
		totalPages: number;
	};
}

export interface CreateMediaDto {
	alt?: string;
	description?: string;
}

@Injectable({
	providedIn: 'root',
})
export class MediaService {
	private apiService = inject(ApiService);
	private http = inject(HttpClient);
	private readonly apiBasePath = '/backOffice/media';

	uploadFile(file: File, metadata?: CreateMediaDto): Observable<MediaItem> {
		const formData = new FormData();
		formData.append('file', file);
		if (metadata?.alt) {
			formData.append('alt', metadata.alt);
		}
		if (metadata?.description) {
			formData.append('description', metadata.description);
		}

		return this.http
			.post<ApiResponse<MediaItem>>(`${environment.apiUrl}${this.apiBasePath}/upload`, formData)
			.pipe(map((response) => response.data));
	}

	getMediaList(options?: { page?: number; limit?: number; search?: string }): Observable<MediaListResponse> {
		let params = new HttpParams();
		if (options?.page) {
			params = params.set('page', options.page.toString());
		}
		if (options?.limit) {
			params = params.set('limit', options.limit.toString());
		}
		if (options?.search) {
			params = params.set('search', options.search);
		}

		return this.apiService.getFullResponse<MediaItem[]>(this.apiBasePath, params).pipe(
			map((response) => {
				// Il backend restituisce data e pagination separati
				const data = response.data || [];
				const pagination = (response as any).pagination || {
					total: 0,
					page: 1,
					limit: 20,
					totalPages: 0,
				};
				return {
					data,
					pagination,
				};
			}),
		);
	}

	getMediaById(id: string): Observable<MediaItem> {
		return this.apiService.get<MediaItem>(`${this.apiBasePath}/${id}`);
	}

	updateMedia(id: string, updateData: { alt?: string; description?: string }): Observable<MediaItem> {
		return this.apiService.patch<MediaItem>(`${this.apiBasePath}/${id}`, updateData);
	}

	deleteMedia(id: string): Observable<void> {
		return this.apiService.delete<void>(`${this.apiBasePath}/${id}`);
	}

	formatFileSize(bytes: number): string {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
	}
}

